/* eslint-disable @atlaskit/platform/use-recommended-utils */
import FeatureGates, { type GetExperimentValueOptions } from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { STATSIG_QUICK_FIND_LAYER } from '@atlassian/search-experiment/constants';
import { getMutuallyExclusiveUIExperiment } from '@atlassian/search-experiment/frontend';
import { type ExperimentConfig } from '@atlassian/search-experiment/types';

import { PrimaryProductKeys } from '../../constants/primary-product-keys';
import { ProductKeys3P } from '../../constants/product-keys';
import type { PrimaryProductKey } from '../../constants/product-keys';
import {
	FEDERATED_CONNECTOR_SOURCE,
	type ThirdPartyConfigsBootstrap,
} from '../../constants/schemas/3p-config';

/**
 * A util to trigger an exposure for the Weak Link experiment.
 */
export const triggerExposureForWeakLinkExp: () => void = (): void => {
	FeatureGates.manuallyLogExperimentExposure('enable_google_drive_impersonation_search_in_fps');
};

/**
 * A centralised util to check if the Right Rail improvements experiment is enabled WITHOUT triggering an exposure.
 * This is useful for cases where we want to check the cohort allocation without logging an exposure.
 */
export const serveWeakLinkExpWithoutExposure: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'enable_google_drive_impersonation_search_in_fps',
			'cohort',
			'control',
			{
				fireExperimentExposure: false,
			},
		) === 'test'
	);
};

export const removeFedSlackFromAllProductSearch = (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	isAllProductSearch: boolean,
): boolean | undefined => {
	const isSlackAuthenticated = !thirdPartyConfigs?.[ProductKeys3P.Slack]?.userNeedsOAuth;
	const hasFederatedConnectorSource = thirdPartyConfigs?.[
		ProductKeys3P.Slack
	]?.connectorSources?.includes(FEDERATED_CONNECTOR_SOURCE);
	const isUserEligible = isSlackAuthenticated && hasFederatedConnectorSource && isAllProductSearch;

	return isUserEligible;
};

export const serveNounResultCounts: (fireExperimentExposure?: boolean) => boolean = (
	fireExperimentExposure = false,
): boolean => {
	return (
		FeatureGates.getExperimentValue<string>('rovosearch_noun_result_counts', 'cohort', 'control', {
			fireExperimentExposure,
		}) === 'test'
	);
};

export const serveWebPagesInTypeFilter: (fireExperimentExposure?: boolean) => boolean = (
	fireExperimentExposure = false,
): boolean => {
	return (
		FeatureGates.getExperimentValue<string>('web_pages_type_filter_bug', 'cohort', 'control', {
			fireExperimentExposure,
		}) === 'test'
	);
};

/**
 * A centralised util to check if the QF filter suggestion experiment is enabled
 */
export const serveQFFilterSuggestionExperiment: (
	primaryProduct?: PrimaryProductKey | null,
	options?: {
		fireLayerExposure?: boolean;
	},
) => boolean = (
	primaryProduct?: PrimaryProductKey | null,
	{ fireLayerExposure = true }: { fireLayerExposure?: boolean } = {},
): boolean => {
	if (primaryProduct !== PrimaryProductKeys.Jira) {
		return false;
	}
	const layer = getMutuallyExclusiveUIExperiment(
		'rovo_qf_filter_suggestion_exp',
		'rovo_search_qf_experience_layer',
		{ fireLayerExposure },
	);
	return (
		layer?.get(
			'isFilterSuggestionEnabled',
			false,
			(value: unknown): value is boolean => typeof value === 'boolean',
		) ?? false
	);
};

// A centralised util to check if the search_user_right_rail_3p_personalization_v1 is enabled and logging an exposure.
export const serveRightRailRecommendationsImprovementsWithExposure: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'search_user_right_rail_3p_personalization_v1',
			'cohort',
			'control',
			{
				fireExperimentExposure: true,
			},
		) === 'test'
	);
};

export const serveQF3PBannerExperiment: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>('quick-find-3p-banner', 'bannerType', 'control') !==
		'control'
	);
};

export const serveQF3PBannerExperimentWithoutExposure: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>('quick-find-3p-banner', 'bannerType', 'control', {
			fireExperimentExposure: false,
		}) !== 'control'
	);
};

export const serveConfluenceSpaceIconUpdate: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<boolean>('confluence_space_icon_update', 'isEnabled', false) ===
		true
	);
};

// Experiment: https://hello.atlassian.net/wiki/x/okE3bgE
export const serve3PClickIngestionExperiment: (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	options?: GetExperimentValueOptions<string>,
) => boolean = (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	options: GetExperimentValueOptions<string> = {
		fireExperimentExposure: true,
	},
): boolean => {
	const isEligible3PUser = Object.keys(thirdPartyConfigs).some(
		(productKey) => thirdPartyConfigs[productKey]?.userNeedsOAuth === false,
	);

	return (
		isEligible3PUser &&
		FeatureGates.getExperimentValue<string>(
			'3p_clicks_in_fps_as_qf_recents',
			'cohort',
			'control',
			options,
		) === 'test'
	);
};

/**
 * Returns whether the `rovo_search_preload_from_quick_find_using_async`
 * experiment is enabled (cohort `isEnabled === true`).
 *
 * Centralised so call sites in the search-page package (config controller,
 * query-plan, Quick Find onNavigate wrapper) can share a single, identical
 * gate read instead of duplicating the inline `FeatureGates.getExperimentValue`
 * call. Always fires an exposure (the default behaviour) — every consumer of
 * this experiment gates real user-facing behaviour on the result.
 */
export const isQuickFindAsyncPreloadEnabled = (): boolean =>
	FeatureGates.getExperimentValue<boolean>(
		'rovo_search_preload_from_quick_find_using_async',
		'isEnabled',
		false,
	) === true;

export const serveSearchCombinedConfigExperiment: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<boolean>('search_combined_config', 'isEnabled', false) === true
	);
};
/**
 * A centralised util to check if the Quick Find 3P boosting experiment is enabled.
 * This experiment prioritizes 3rd party documents in the recent activities section.
 * Note: This is a standalone experiment (not mutually exclusive with other Quick Find experiments).
 */
export const serveQuickFind3PBoostingExperiment: (options?: {
	fireExperimentExposure?: boolean;
}) => boolean = ({
	fireExperimentExposure = false,
}: {
	fireExperimentExposure?: boolean;
} = {}): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'quick_find_3p_boosting_experiment',
			'cohort',
			'control',
			{
				fireExperimentExposure,
			},
		) === 'test'
	);
};

// Experiment: https://switcheroo.atlassian.com/ui/gates/ca4413ca-ac7e-4b41-a744-cd86b29d783c/key/afm_customer_contact_search_type_experiment
export const serveCustomerContactEntityType: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'afm_customer_contact_search_type_experiment',
			'cohort',
			'control',
		) === 'test'
	);
};

/**
 * A centralised util to check if the experiment is enabled WITHOUT triggering an exposure.
 * https://switcheroo.atlassian.com/ui/gates/03ee4670-38f1-4cd1-bc84-b4999e2d9469/key/rovo_search_improve_engagement_tracking
 */
export const serveEngagementTrackingImprovementsExperiment: () => boolean = (): boolean => {
	// Check the targeting FG first. This is technically not needed, but makes VR testing much easier
	// since the Gemini framework has full support for FGs, but not Statsig experiments.
	if (!fg('rovo_search_improve_engagement_tracking_gate')) {
		return false;
	}
	return (
		FeatureGates.getExperimentValue<string>(
			'rovo_search_improve_engagement_tracking',
			'cohort',
			'control',
			{
				fireExperimentExposure: false,
			},
		) === 'test'
	);
};

/**
 * A util to trigger an exposure for the experiment.
 * https://switcheroo.atlassian.com/ui/gates/03ee4670-38f1-4cd1-bc84-b4999e2d9469/key/rovo_search_improve_engagement_tracking
 */
export const triggerExposureForEngagementTrackingImprovementsExperiment: () => void = (): void => {
	// Check the targeting FG first. This is technically not needed, but makes VR testing much easier
	// since the Gemini framework has full support for FGs, but not Statsig experiments.
	if (!fg('rovo_search_improve_engagement_tracking_gate')) {
		return;
	}
	FeatureGates.manuallyLogExperimentExposure('rovo_search_improve_engagement_tracking');
};

/**
 * >>> Confluence QuickFind UserShard Search Experiments
 *
 * We have 2 separated experiments: one is for hello tenant, the other is for 24 non-hello tenants.
 *
 * Experiment: `quick-find-user-shard-search-experiment`
 * + hello only
 * + statsig_url = https://go/statsig/experiments/quick-find-user-shard-search-experiment
 *
 * Experiment: `confluence-quickfind-usershard-nonhello-exp`
 * + 24 non-hello tenants
 * + statsig_url = https://go/statsig/experiments/confluence-quickfind-usershard-nonhello-exp
 *
 * These experiments have 3 groups:
 * + cohort = 'control', the current product.
 * + cohort = 'model-only', the model is served without any changes to the UI.
 * + cohort = 'test', both the model and new UI are served.
 *
 */

const USER_SHARD_EXPERIMENTS = [
	'quick-find-user-shard-search-experiment',
	'confluence-quickfind-usershard-nonhello-exp',
] as const;

type UserShardExperimentDetails = {
	cohort?: string;
	spaceL1Id?: string;
	pageL1Id?: string;
	debounceTimeout?: number;
};

const getValidDebounceTimeout = (value?: unknown): number | undefined => {
	if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) {
		return undefined;
	}

	return value >= 0 ? value : undefined;
};

const getUserShardExperimentDetails = (): UserShardExperimentDetails => {
	for (const experimentName of USER_SHARD_EXPERIMENTS) {
		const experiment = getMutuallyExclusiveUIExperiment(experimentName, STATSIG_QUICK_FIND_LAYER, {
			fireLayerExposure: false,
		});
		if (experiment !== undefined) {
			return {
				cohort: experiment.get<string | undefined>('cohort', undefined),
				spaceL1Id: experiment.get<string | undefined>('spaceL1Id', undefined),
				pageL1Id: experiment.get<string | undefined>('pageL1Id', undefined),
				debounceTimeout: getValidDebounceTimeout(
					experiment.get<number | undefined>('debounceTimeout', undefined),
				),
			};
		}
	}

	return {
		cohort: undefined,
		spaceL1Id: undefined,
		pageL1Id: undefined,
		debounceTimeout: undefined,
	};
};

/**
 * Returns all user shard experiment flags in a single pass.
 * More efficient when multiple flags are needed simultaneously.
 */
export const getUserShardExperimentFlags = (): {
	serveModel: boolean;
	serveUI: boolean;
	serveControl: boolean;
	cohort: string | undefined;
} => {
	const { cohort } = getUserShardExperimentDetails();

	return {
		serveModel: cohort === 'model-only' || cohort === 'test',
		serveUI: cohort === 'test',
		serveControl: cohort === 'control',
		cohort, // Expose for debugging/logging
	};
};

export const serveUserShardModel = (): boolean => {
	return getUserShardExperimentFlags().serveModel;
};

export const serveUserShardUI = (): boolean => {
	return getUserShardExperimentFlags().serveUI;
};

export const serveUserShardControl = (): boolean => {
	return getUserShardExperimentFlags().serveControl;
};

export const getUserShardSpaceL1Id = (): string | undefined => {
	return getUserShardExperimentDetails().spaceL1Id;
};

export const getUserShardPageL1Id = (): string | undefined => {
	return getUserShardExperimentDetails().pageL1Id;
};

export const getUserShardDebounceTimeout = (): number | undefined => {
	return getUserShardExperimentDetails().debounceTimeout;
};

export const getMultiSiteSearchInRovo: (experimentConfig: ExperimentConfig) => boolean = (
	experimentConfig: ExperimentConfig,
): boolean => {
	const cohort = experimentConfig.frontendExperiments?.['multi_site_search_in_rovo']?.cohort;

	return cohort === 'test';
};

/**
 * A centralised util to check if the one-click 3P connect experiment is enabled.
 * This experiment skips the confirmation modal when connecting to third-party apps.
 * Experiment: https://switcheroo.atlassian.com/ui/gates/16fc971c-5f65-4d37-9b6d-210856883bb8/key/unified_search_one_click_3p_connect
 */
export const serveOneClick3PConnectExperiment: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<boolean>(
			'unified_search_one_click_3p_connect',
			'isEnabled',
			false,
		) === true
	);
};

/**
 * Unified Search 3P Connect Improvements Experiment
 */

const UNIFIED_SEARCH_3P_CONNECT_IMPROVEMENTS_TEST_COHORTS = [
	'icon-and-tooltip',
	'icon-only',
	'tooltip-only',
] as const;

export const UNIFIED_SEARCH_3P_CONNECT_IMPROVEMENTS_COHORTS: readonly [
	'control',
	'icon-and-tooltip',
	'icon-only',
	'tooltip-only',
] = ['control', ...UNIFIED_SEARCH_3P_CONNECT_IMPROVEMENTS_TEST_COHORTS] as const;

type UnifiedSearch3PConnectImprovementsCohort =
	(typeof UNIFIED_SEARCH_3P_CONNECT_IMPROVEMENTS_COHORTS)[number];

/**
 * A centralised util to check the cohort for the unified search 3P connect improvements experiment.
 * This experiment adds a "link-out" icon to unconnected 3P app filters OR a tooltip with context on hover OR both, depeding on the cohort.
 * Experiment: https://switcheroo.atlassian.com/ui/gates/06441b13-168d-4a96-991d-b1dc9e3273dc/key/unified_search_3p_connect_improvements
 */

export const getUnifiedSearch3PConnectImprovementsCohort =
	(): UnifiedSearch3PConnectImprovementsCohort => {
		const cohort = FeatureGates.getExperimentValue<string>(
			'unified_search_3p_connect_improvements',
			'cohort',
			'control',
		);

		if (
			UNIFIED_SEARCH_3P_CONNECT_IMPROVEMENTS_COHORTS.includes(
				cohort as UnifiedSearch3PConnectImprovementsCohort,
			)
		) {
			return cohort as UnifiedSearch3PConnectImprovementsCohort;
		}

		return 'control';
	};

/**
 * Checks if the user is in a test cohort for the unified search 3P connect improvements experiment.
 * Returns true for test cohorts (icon-and-tooltip, icon-only, tooltip-only).
 * Returns false for control cohorts (control, not-enrolled).
 * This ensures holdout users (not-enrolled) are not treated as test cohort users.
 */
export const isInUnifiedSearch3PConnectImprovementsTestCohort = (): boolean => {
	const cohort = getUnifiedSearch3PConnectImprovementsCohort();
	return UNIFIED_SEARCH_3P_CONNECT_IMPROVEMENTS_TEST_COHORTS.includes(
		cohort as (typeof UNIFIED_SEARCH_3P_CONNECT_IMPROVEMENTS_TEST_COHORTS)[number],
	);
};

const IMPROVE_BANNER_TARGETING_COHORTS = ['control', 'usage-threshold', 'max-dismissals'] as const;
export type ImproveBannerTargetingCohort = (typeof IMPROVE_BANNER_TARGETING_COHORTS)[number];

/**
 * A centralised util to check the improve-banner-targeting experiment cohort.
 *
 * Cohort 1 ('usage-threshold'): Enforce a minimum product usage threshold before showing the OAuth banner.
 * Cohort 2 ('max-dismissals'): Apply isHello-specific dismissal logic (cross-product max dismissals) for all users.
 *
 * Experiment: https://switcheroo.atlassian.com/ui/gates/0cfc0b4b-2fad-46e6-9d6f-247dbfdbee02/key/rovo_search_improve_banner_targeting
 */
export const serveImproveBannerTargetingExperiment = ({
	fireExperimentExposure = true,
}: {
	fireExperimentExposure?: boolean;
} = {}): ImproveBannerTargetingCohort => {
	const cohort = FeatureGates.getExperimentValue<string>(
		'rovo_search_improve_banner_targeting',
		'cohort',
		'control',
		{ fireExperimentExposure },
	);

	if (IMPROVE_BANNER_TARGETING_COHORTS.includes(cohort as ImproveBannerTargetingCohort)) {
		return cohort as ImproveBannerTargetingCohort;
	}

	return 'control';
};

/*
 * A centralised util to check if the Quick Find zero query allocation experiment is enabled.
 * This experiment contains
 * 1. Splits the pre-query recommendations into separate "Recommended" and "Recent" sections.
 * 2. Remove metadata and move right metadata stick next to title
 * 3. Changes the allocation between "Recommended" and "Recent"
 * 	  - Recommended: 4 -> 3
 * 	  - Recent: 4 -> 5
 *
 * Switcheroo: https://switcheroo.atlassian.com/ui/gates/0020f286-4964-4606-ae19-fc1f4639f2b9/key/quick_find_zero_query_allocation_experiment
 * Experiment: https://hello.atlassian.net/wiki/spaces/CentAI/pages/6488921517/Quick+Find+Zero+Query+Allocation+Experiment
 */
export const serveQFZeroQueryAllocationExperiment = (
	options: GetExperimentValueOptions<string> = {
		fireExperimentExposure: false,
	},
): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'quick_find_zero_query_allocation_experiment',
			'cohort',
			'control',
			options,
		) === 'test'
	);
};

const INLINE_3P_QUERY_SUGGESTIONS_TREATMENTS = ['control', 'static', 'dynamic'] as const;
export type Inline3PQuerySuggestionsTreatment =
	(typeof INLINE_3P_QUERY_SUGGESTIONS_TREATMENTS)[number];

/**
 * A centralised util to check the inline 3P query suggestions experiment treatment.
 * This experiment enables inline third-party query suggestions in the search experience.
 *
 * Treatment 'static': Shows static inline query suggestions.
 * Treatment 'dynamic': Shows static + dynamic inline query suggestions.
 *
 * Experiment: https://switcheroo.atlassian.com/ui/gates/3cc0553d-17c2-4609-93e1-e6ba3aaf6aec/key/inline_3p_query_suggestions_phase_1
 */
export const serveInline3PQuerySuggestionsPhase1 = ({
	fireExperimentExposure = true,
}: {
	fireExperimentExposure?: boolean;
} = {}): Inline3PQuerySuggestionsTreatment => {
	const treatment = FeatureGates.getExperimentValue<string>(
		'inline_3p_query_suggestions_phase_1',
		'inline_3p_query_suggestions_treatment',
		'control',
		{ fireExperimentExposure },
	);

	if (
		INLINE_3P_QUERY_SUGGESTIONS_TREATMENTS.includes(treatment as Inline3PQuerySuggestionsTreatment)
	) {
		return treatment as Inline3PQuerySuggestionsTreatment;
	}

	return 'control';
};

/**
 * A centralised util to get the experiment value for the unified search right rail enhancements experiment.
 * This experiment separates connected products from unconnected products in the right panel and involves 3 cohorts/variations:
 * 1. control - UI before experiment with app filters combined
 * 2. single-select - shows the connected and unconnected products in separate sections with single-select app filters
 * 3. multi-select - shows the connected and unconnected products in separate sections with multi-select app filters
 * Experiment: https://switcheroo.atlassian.com/ui/gates/2db9f352-e361-4337-9d3e-395a68a87d57/key/unified_search_right_rail_enhancements
 */
export const getUnifiedSearchRightRailEnhancementsExpValue = (): string => {
	return FeatureGates.getExperimentValue<string>(
		'unified_search_right_rail_enhancements',
		'cohort',
		'control',
	);
};

/**
 * A centralised util to check if the search settings modal experiment is enabled.
 *
 * Pass `fireExperimentExposure: false` for render-time decisions where the entry
 * point looks identical between treatment and control — exposure fires later via
 * `triggerExposureForSearchSettingsModalExperiment` at click-time.
 *
 * Omit (defaults to `true`) when the rendered element is itself the treatment
 * (e.g. a new UI element only visible to the treatment cohort).
 *
 * Experiment: https://switcheroo.atlassian.com/ui/gates/ed054974-134e-4179-85a9-0f743a30fd72/key/rovo_search_settings_modal_enabled
 */
export const serveSearchSettingsModalExperiment = ({
	fireExperimentExposure = true,
}: {
	fireExperimentExposure?: boolean;
} = {}): boolean =>
	FeatureGates.getExperimentValue('rovo_search_settings_modal_enabled', 'isEnabled', false, {
		fireExperimentExposure,
	});

/**
 * A util to manually fire exposure for the search settings modal experiment.
 * Experiment: https://switcheroo.atlassian.com/ui/gates/ed054974-134e-4179-85a9-0f743a30fd72/key/rovo_search_settings_modal_enabled
 */
export const triggerExposureForSearchSettingsModalExperiment = (): void => {
	FeatureGates.manuallyLogExperimentExposure('rovo_search_settings_modal_enabled');
};

/**
 * Experiment with surfacing 3p docs (google docs and microsoft sharepoint) in recommendations section of QF, and improved 1p docs relevance.
 * 1. isEnabled: true -> surface 3p docs in recs and improved relevance for 1p docs
 * 2. isEnabled: false -> control experience
 * Experiment: https://switcheroo.atlassian.com/ui/gates/d3d5d238-7efa-4eaa-a83d-bc2ac8698633/key/quick_find_zero_query_3p_recs_experiment
 */
export const serveQFZeroQuery3PRecsExperiment = (
	options: GetExperimentValueOptions<boolean> = {
		fireExperimentExposure: false,
	},
): boolean => {
	return (
		FeatureGates.getExperimentValue<boolean>(
			'quick_find_zero_query_3p_recs_experiment',
			'isEnabled',
			false,
			options,
		) === true
	);
};

/**
 * A centralised util to check if the GitHub PR recents experiment is enabled.
 *
 * Pass `fireExperimentExposure: false` when checking for allocation (e.g. building the query),
 * and `fireExperimentExposure: true` when the user is actually exposed to the experience
 * (i.e. when the Quick Find dialog mounts and the user has GitHub connector access).
 *
 * Experiment: https://switcheroo.atlassian.com/ui/gates/08b879cf-4d1b-4910-848e-d9592db8acd7/key/rovo_search_github_in_recents
 */
export const serveGithubRecentsExperiment = ({
	fireExperimentExposure = false,
}: {
	fireExperimentExposure?: boolean;
} = {}): boolean => {
	return (
		FeatureGates.getExperimentValue<string>('rovo_search_github_in_recents', 'cohort', 'control', {
			fireExperimentExposure,
		}) === 'test'
	);
};

export const serveSlClickedOnlyInRecentsExperiment = ({
	fireExperimentExposure = false,
}: {
	fireExperimentExposure?: boolean;
} = {}): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'rovo_search_use_sl_clicked_only_in_recents',
			'cohort',
			'control',
			{ fireExperimentExposure },
		) === 'test'
	);
};

const FULLPAGE_SEARCH_SUMMARIZE_BUTTON_REVAMP_TREATMENTS = [
	'control',
	'visibility',
	'border',
] as const;
export type FullpageSearchSummarizeButtonRevampTreatment =
	(typeof FULLPAGE_SEARCH_SUMMARIZE_BUTTON_REVAMP_TREATMENTS)[number];

/**
 * A centralised util to check the Full Page Search Summarize Button Revamp experiment treatment.
 * This experiment revamps the summarize button in Full Page Search results.
 *
 * - Treatment 'visibility': improved button visibility
 * - Treatment 'border': improved button visibility with a branded border
 *
 * Experiment: https://switcheroo.atlassian.com/ui/gates/e6b25656-9bdd-449e-858f-b1d2ba7a3a14/key/fullpage_search_summarize_button_revamp
 */
export const serveFullpageSearchSummarizeButtonRevamp = ({
	fireExperimentExposure = true,
}: {
	fireExperimentExposure?: boolean;
} = {}): FullpageSearchSummarizeButtonRevampTreatment => {
	const treatment = FeatureGates.getExperimentValue<string>(
		'fullpage_search_summarize_button_revamp',
		'fullpage_search_summarize_button_revamp_treatment',
		'control',
		{ fireExperimentExposure },
	);

	if (
		FULLPAGE_SEARCH_SUMMARIZE_BUTTON_REVAMP_TREATMENTS.includes(
			treatment as FullpageSearchSummarizeButtonRevampTreatment,
		)
	) {
		return treatment as FullpageSearchSummarizeButtonRevampTreatment;
	}

	return 'control';
};
